import { INTEGER_REGEX_EXP, METRIC_FILTER_NUMBER_REGEX_EXP, METRIC_FILTER_OPERATOR_REGEX_EXP, METRIC_FILTER_ORDER_VALUES, METRIC_FILTER_QUANTILE_SIZE_INTEGER_MAP, METRIC_FILTER_QUANTILE_SIZE_VALUES, METRIC_FILTER_TOP_OR_BOTTOM_VALUES, TOKEN_IS_EDITING, } from 'ScreenerApp/ts/features/screener/constants';
export const TOKEN_TYPE = {
    METRIC: 'METRIC',
    OPERATOR: 'OPERATOR',
    NUMBER: 'NUMBER',
    OPENING_PARENTHESIS: 'OPENING_PARENTHESIS',
    CLOSING_PARENTHESIS: 'CLOSING_PARENTHESIS',
    IS_EDITING: 'IS_EDITING',
    BAD_INPUT: 'BAD_INPUT',
};
export const VALIDATION_STATUS = {
    INCOMPLETE: 'INCOMPLETE',
    INVALID: 'INVALID',
    VALID: 'VALID',
};
export const INVALID_REASON = {
    SEPARATE_OPERATORS: 'SEPARATE_OPERATORS',
    SEPARATE_METRICS: 'SEPARATE_METRICS',
    SEPARATE_PARENTHESES: 'SEPARATE_PARENTHESES',
    SEPARATE_OPEN_PARENTHESIS: 'SEPARATE_OPEN_PARENTHESIS',
    SEPARATE_CLOSING_PARENTHESIS: 'SEPARATE_CLOSING_PARENTHESIS',
    STARTING_CLOSING_PARENTHESIS: 'STARTING_CLOSING_PARENTHESIS',
    BAD_INPUT: 'BAD_INPUT',
    PARENTHESIS_BALANCE: 'PARENTHESIS_BALANCE',
    PERCENTILE_CHOICES_INVALID: 'PERCENTILE_CHOICES_INVALID',
    PERCENTILE_ORDINAL_INVALID: 'PERCENTILE_ORDINAL_INVALID',
    RANK_CHOICES_INVALID: 'RANK_CHOICES_INVALID',
    RANK_ORDINAL_INVALID: 'RANK_ORDINAL_INVALID',
    BETWEEN_RANGE: 'BETWEEN_RANGE',
    MINIMUM_METRIC: 'MINIMUM_METRIC',
};
const identifyToken = (token, metricLabelMap) => {
    if (token === undefined)
        return undefined;
    if (token === '(')
        return TOKEN_TYPE.OPENING_PARENTHESIS;
    if (token === ')')
        return TOKEN_TYPE.CLOSING_PARENTHESIS;
    if (token === TOKEN_IS_EDITING)
        return TOKEN_TYPE.IS_EDITING;
    if (METRIC_FILTER_OPERATOR_REGEX_EXP.test(token))
        return TOKEN_TYPE.OPERATOR;
    if (METRIC_FILTER_NUMBER_REGEX_EXP.test(token))
        return TOKEN_TYPE.NUMBER;
    if (token in metricLabelMap)
        return TOKEN_TYPE.METRIC;
    // if it's none of the above, it has to be invalid input.
    return TOKEN_TYPE.BAD_INPUT;
};
/*
 *  validateMetricFilterPartial - function that performs validation on a list of tokens. Short-circuits as
 *                                soon as it finds an invalid token or reason for incompleteness.
 *
 *  `returns` a validation object:
 *  {
 *      status: `INVALID` || `INCOMPLETE` || `VALID`,
 *      index: `int` or `null`,
 *      reason: choice of `INVALID_REASON` or `null`
 *  }
 *
 */
export const validateMetricFilterPartial = (equationTokens, metricLabelMap) => {
    let index;
    let precedingToken;
    let precedingTokenIdentity;
    let token;
    let tokenIdentity;
    /*
     * if 0 tokens in expression, it is incomplete.
     * */
    if (equationTokens.length === 0) {
        return {
            index: null,
            status: VALIDATION_STATUS.INCOMPLETE,
            reason: null,
        };
    }
    /*
     * Validate adjacent token pairs.
     *
     *           Note: the first item in the expression has special cases for validation.
     *           In those cases, we check for index being 0 to determine if the token in question
     *           is at the start of the expression.
     *
     * */
    let openParenthesisCount = 0;
    let closeParenthesisCount = 0;
    for (index = 0; index < equationTokens.length; index += 1) {
        precedingToken = equationTokens[index - 1];
        precedingTokenIdentity = identifyToken(precedingToken, metricLabelMap);
        token = equationTokens[index];
        tokenIdentity = identifyToken(token, metricLabelMap);
        switch (tokenIdentity) {
            // METRIC & NUMBER VALID IF: preceded by operator, opening parenthesis, or start of expression
            case TOKEN_TYPE.METRIC:
            case TOKEN_TYPE.NUMBER:
                if (precedingTokenIdentity !== TOKEN_TYPE.OPERATOR &&
                    precedingTokenIdentity !== TOKEN_TYPE.OPENING_PARENTHESIS &&
                    index !== 0) {
                    return {
                        index,
                        status: VALIDATION_STATUS.INVALID,
                        reason: INVALID_REASON.SEPARATE_METRICS,
                    };
                }
                break;
            // OPENING PARENTHESIS VALID IF: preceded by operator, opening parenthesis, or start of expression
            case TOKEN_TYPE.OPENING_PARENTHESIS:
                openParenthesisCount += 1;
                if (precedingTokenIdentity !== TOKEN_TYPE.OPERATOR &&
                    precedingTokenIdentity !== TOKEN_TYPE.OPENING_PARENTHESIS &&
                    index !== 0) {
                    return {
                        index,
                        status: VALIDATION_STATUS.INVALID,
                        reason: INVALID_REASON.SEPARATE_OPEN_PARENTHESIS,
                    };
                }
                break;
            // CLOSING PARENTHESIS VALID IF: preceded by metric, number, closing parenthesis
            case TOKEN_TYPE.CLOSING_PARENTHESIS:
                closeParenthesisCount += 1;
                if (precedingTokenIdentity !== TOKEN_TYPE.METRIC &&
                    precedingTokenIdentity !== TOKEN_TYPE.NUMBER &&
                    precedingTokenIdentity !== TOKEN_TYPE.CLOSING_PARENTHESIS) {
                    const reason = index === 0
                        ? INVALID_REASON.STARTING_CLOSING_PARENTHESIS
                        : INVALID_REASON.SEPARATE_CLOSING_PARENTHESIS;
                    return {
                        index,
                        status: VALIDATION_STATUS.INVALID,
                        reason,
                    };
                }
                break;
            // OPERATOR VALID IF: preceded by metric, number, closing parenthesis
            case TOKEN_TYPE.OPERATOR:
                if (precedingTokenIdentity === TOKEN_TYPE.METRIC ||
                    precedingTokenIdentity === TOKEN_TYPE.NUMBER ||
                    precedingTokenIdentity === TOKEN_TYPE.CLOSING_PARENTHESIS) {
                    // SPECIAL CASE: if the last item in the equation is an operator,
                    // the equation is incomplete, otherwise it is valid.
                    if (index + 1 === equationTokens.length) {
                        return {
                            index,
                            status: VALIDATION_STATUS.INCOMPLETE,
                            reason: null,
                        };
                    }
                }
                else {
                    return {
                        index,
                        status: VALIDATION_STATUS.INVALID,
                        reason: INVALID_REASON.SEPARATE_OPERATORS,
                    };
                }
                break;
            // IS_EDITING ALWAYS INCOMPLETE
            case TOKEN_TYPE.IS_EDITING:
                return {
                    index,
                    status: VALIDATION_STATUS.INCOMPLETE,
                    reason: null,
                };
            // BAD INPUT ALWAYS INVALID
            case TOKEN_TYPE.BAD_INPUT:
                return {
                    index,
                    status: VALIDATION_STATUS.INVALID,
                    reason: INVALID_REASON.BAD_INPUT,
                };
            // no default
        }
    }
    /*
     * Check Parenthesis balance
     * */
    if (openParenthesisCount > closeParenthesisCount) {
        return {
            index: null,
            status: VALIDATION_STATUS.INCOMPLETE,
            reason: null,
        };
    }
    if (openParenthesisCount < closeParenthesisCount) {
        return {
            index: null,
            status: VALIDATION_STATUS.INVALID,
            reason: INVALID_REASON.PARENTHESIS_BALANCE,
        };
    }
    return {
        index: null,
        status: VALIDATION_STATUS.VALID,
        reason: null,
    };
};
/*
 * Validate Percentile
 */
export const validatePercentilePartialState = (rightMetricFilterPartial) => {
    const [topOrBottom, ordinal, quantileSize, order] = rightMetricFilterPartial;
    if (ordinal === null) {
        return {
            index: null,
            status: VALIDATION_STATUS.INCOMPLETE,
            reason: null,
        };
    }
    // validate that `topOrBottom`, `quantileSize` and `order` are valid values.
    const validOrder = METRIC_FILTER_ORDER_VALUES.indexOf(order) !== -1;
    const validTopOrBottom = METRIC_FILTER_TOP_OR_BOTTOM_VALUES.indexOf(topOrBottom) !== -1;
    const validQuantileSize = METRIC_FILTER_QUANTILE_SIZE_VALUES.indexOf(quantileSize) !== -1;
    const validChoices = validOrder && validTopOrBottom && validQuantileSize;
    if (!validChoices) {
        return {
            index: null,
            status: VALIDATION_STATUS.INVALID,
            reason: INVALID_REASON.PERCENTILE_CHOICES_INVALID,
        };
    }
    // validate that `ordinal` falls within the correct range
    const ordinalIsInteger = INTEGER_REGEX_EXP.test(ordinal);
    let validOrdinal = ordinalIsInteger;
    if (ordinalIsInteger) {
        const ordinalAsInt = Number(ordinal);
        // @ts-ignore
        validOrdinal = ordinalAsInt > 0 && ordinalAsInt < METRIC_FILTER_QUANTILE_SIZE_INTEGER_MAP[quantileSize];
    }
    if (!validOrdinal) {
        return {
            index: null,
            status: VALIDATION_STATUS.INVALID,
            reason: INVALID_REASON.PERCENTILE_ORDINAL_INVALID,
        };
    }
    return {
        index: null,
        status: VALIDATION_STATUS.VALID,
        reason: null,
    };
};
/**
 * Validate Rank metric filters
 */
export const validateRankPartialState = (rightMetricFilterPartial) => {
    const [topOrBottom, ordinal, order] = rightMetricFilterPartial;
    if (ordinal === null) {
        return {
            index: null,
            status: VALIDATION_STATUS.INCOMPLETE,
            reason: null,
        };
    }
    // validate that `topOrBottom`, and `order` are valid values.
    const validOrder = METRIC_FILTER_ORDER_VALUES.indexOf(order) !== -1;
    const validTopOrBottom = METRIC_FILTER_TOP_OR_BOTTOM_VALUES.indexOf(topOrBottom) !== -1;
    const validChoices = validOrder && validTopOrBottom;
    if (!validChoices) {
        return {
            index: null,
            status: VALIDATION_STATUS.INVALID,
            reason: INVALID_REASON.RANK_CHOICES_INVALID,
        };
    }
    // validate that `ordinal` falls within the correct range
    const ordinalIsInteger = INTEGER_REGEX_EXP.test(ordinal);
    const validOrdinal = ordinalIsInteger && Number(ordinal) > 0;
    if (!validOrdinal) {
        return {
            index: null,
            status: VALIDATION_STATUS.INVALID,
            reason: INVALID_REASON.RANK_ORDINAL_INVALID,
        };
    }
    return {
        index: null,
        status: VALIDATION_STATUS.VALID,
        reason: null,
    };
};
/**
 * Validate Range metric filter
 */
export const validateRangePartialState = (rightMetricFilterPartial) => {
    const [min, max] = rightMetricFilterPartial;
    if (min === null || max === null) {
        return {
            index: null,
            status: VALIDATION_STATUS.INCOMPLETE,
            reason: null,
        };
    }
    const rangeIsNumeric = METRIC_FILTER_NUMBER_REGEX_EXP.test(min) && METRIC_FILTER_NUMBER_REGEX_EXP.test(max);
    const rangeIsValid = rangeIsNumeric && Number(min) < Number(max);
    if (!rangeIsValid) {
        return {
            index: null,
            status: VALIDATION_STATUS.INVALID,
            reason: INVALID_REASON.BETWEEN_RANGE,
        };
    }
    return {
        index: null,
        status: VALIDATION_STATUS.VALID,
        reason: null,
    };
};
/**
 * Global validation (`left` and `right` together)
 */
const containsTokenType = (metricFilterPartial, tokenType, metricLabelMap) => {
    let tokenIdentity;
    for (let i = 0; i < metricFilterPartial.length; i += 1) {
        tokenIdentity = identifyToken(metricFilterPartial[i], metricLabelMap);
        if (tokenIdentity === tokenType) {
            return true;
        }
    }
    return false;
};
export const validateMetricFilterExpression = (leftMetricFilterPartial, rightMetricFilterPartial, metricLabelMap) => {
    // both sides must be populated.
    const partialIsEmpty = !leftMetricFilterPartial.length || !rightMetricFilterPartial.length;
    const leftIsEditing = containsTokenType(leftMetricFilterPartial, TOKEN_TYPE.IS_EDITING, metricLabelMap);
    const rightIsEditing = containsTokenType(rightMetricFilterPartial, TOKEN_TYPE.IS_EDITING, metricLabelMap);
    if (partialIsEmpty || leftIsEditing || rightIsEditing) {
        return {
            index: null,
            status: VALIDATION_STATUS.INCOMPLETE,
            reason: null,
        };
    }
    // across both, they must contain at least 1 financial metric.
    const leftContainsMetric = containsTokenType(leftMetricFilterPartial, TOKEN_TYPE.METRIC, metricLabelMap);
    const rightContainsMetric = containsTokenType(rightMetricFilterPartial, TOKEN_TYPE.METRIC, metricLabelMap);
    if (!(leftContainsMetric || rightContainsMetric)) {
        return {
            index: null,
            status: VALIDATION_STATUS.INVALID,
            reason: INVALID_REASON.MINIMUM_METRIC,
        };
    }
    return {
        index: null,
        status: VALIDATION_STATUS.VALID,
        reason: null,
    };
};
/*
 * Validate if a token can be added to an existing expression.
 *
 *       Note: this logic is similar to `validateMetricFilterPartial` yet different enough
 *       that they ought to remain separate.
 */
export const validateProposedTokenToAdd = (metricFilterPartial, newToken, metricLabelMap) => {
    const precedingToken = metricFilterPartial[metricFilterPartial.length - 1];
    const precedingTokenIdentity = identifyToken(precedingToken, metricLabelMap);
    const proposedTokenIdentity = identifyToken(newToken, metricLabelMap);
    // const isStartOfExpression = metricFilterPartial.length === 0;
    switch (precedingTokenIdentity) {
        // START OF EXPRESSION VALID IF: followed by metric, number, opening parenthesis.
        case undefined:
            if (proposedTokenIdentity !== TOKEN_TYPE.METRIC &&
                proposedTokenIdentity !== TOKEN_TYPE.NUMBER &&
                proposedTokenIdentity !== TOKEN_TYPE.OPENING_PARENTHESIS) {
                let reason;
                if (proposedTokenIdentity === TOKEN_TYPE.CLOSING_PARENTHESIS) {
                    reason = INVALID_REASON.STARTING_CLOSING_PARENTHESIS;
                }
                else if (proposedTokenIdentity === TOKEN_TYPE.OPERATOR) {
                    reason = INVALID_REASON.SEPARATE_OPERATORS;
                }
                else {
                    reason = INVALID_REASON.BAD_INPUT;
                }
                return {
                    index: null,
                    status: VALIDATION_STATUS.INVALID,
                    reason,
                };
            }
            break;
        // METRIC & NUMBER VALID IF: followed by operator or closing parenthesis
        case TOKEN_TYPE.METRIC:
        case TOKEN_TYPE.NUMBER:
            if (proposedTokenIdentity !== TOKEN_TYPE.OPERATOR &&
                proposedTokenIdentity !== TOKEN_TYPE.CLOSING_PARENTHESIS) {
                return {
                    index: null,
                    status: VALIDATION_STATUS.INVALID,
                    reason: INVALID_REASON.SEPARATE_METRICS,
                };
            }
            break;
        // OPENING PARENTHESIS VALID IF: followed by metric, number or opening parenthesis
        case TOKEN_TYPE.OPENING_PARENTHESIS:
            if (proposedTokenIdentity !== TOKEN_TYPE.METRIC &&
                proposedTokenIdentity !== TOKEN_TYPE.NUMBER &&
                proposedTokenIdentity !== TOKEN_TYPE.OPENING_PARENTHESIS) {
                return {
                    index: null,
                    status: VALIDATION_STATUS.INVALID,
                    reason: INVALID_REASON.SEPARATE_OPEN_PARENTHESIS,
                };
            }
            break;
        // CLOSING PARENTHESIS VALID IF: followed by operator, closing parenthesis
        case TOKEN_TYPE.CLOSING_PARENTHESIS:
            if (proposedTokenIdentity !== TOKEN_TYPE.OPERATOR &&
                proposedTokenIdentity !== TOKEN_TYPE.CLOSING_PARENTHESIS) {
                return {
                    index: null,
                    status: VALIDATION_STATUS.INVALID,
                    reason: INVALID_REASON.SEPARATE_CLOSING_PARENTHESIS,
                };
            }
            break;
        // OPERATOR VALID IF: followed by metric, number, opening parenthesis
        case TOKEN_TYPE.OPERATOR:
            if (proposedTokenIdentity !== TOKEN_TYPE.METRIC &&
                proposedTokenIdentity !== TOKEN_TYPE.NUMBER &&
                proposedTokenIdentity !== TOKEN_TYPE.OPENING_PARENTHESIS) {
                return {
                    index: null,
                    status: VALIDATION_STATUS.INVALID,
                    reason: INVALID_REASON.SEPARATE_OPERATORS,
                };
            }
            break;
        // BAD INPUT ALWAYS INVALID
        case TOKEN_TYPE.BAD_INPUT:
            return {
                index: null,
                status: VALIDATION_STATUS.INVALID,
                reason: INVALID_REASON.BAD_INPUT,
            };
        // IS_EDITING ALWAYS allowed.
        case TOKEN_TYPE.IS_EDITING:
            break;
        // no default
    }
    return {
        index: null,
        status: VALIDATION_STATUS.VALID,
        reason: null,
    };
};
export const allowNextTokenToBeOperator = (precedingToken, metricLabelMap) => {
    if (precedingToken === undefined)
        return false;
    const precedingTokenIdentity = identifyToken(precedingToken, metricLabelMap);
    return (precedingTokenIdentity === TOKEN_TYPE.METRIC ||
        precedingTokenIdentity === TOKEN_TYPE.NUMBER ||
        precedingTokenIdentity === TOKEN_TYPE.CLOSING_PARENTHESIS);
};
